import React from 'react';
import { formatDateTimeNl } from '../../utils/formatDateTime';
import { signupSourceLabel } from '../../utils/marketingSignup';

function popupStatusLabel(popup) {
  if (!popup) return 'Onbekend';
  if (popup.status === 'ingeschreven') return 'Ingeschreven — code aangevraagd';
  if (popup.status === 'gesloten') return 'Getoond en gesloten, geen code aangevraagd';
  if (popup.status === 'getoond') return 'Getoond, niet als gesloten geregistreerd';
  if (popup.status === 'geen_browser') return 'Geen browser gekoppeld aan dit e-mailadres';
  return 'Niet getoond op de browsers van deze klant';
}

function profileLabel(type) {
  if (type === 'customer') return 'Klant';
  if (type === 'lead') return 'Lead (nieuwsbrief)';
  if (type === 'visitor') return 'Bezoeker';
  return 'Onbekend';
}

function pathFromBezoek(b) {
  if (!b) return '—';
  if (b.url) {
    try {
      return new URL(b.url).pathname;
    } catch {
      return b.url;
    }
  }
  return b.landing_page || '—';
}

/** Sessies: gap > 30 min = nieuwe sessie (zelfde logica als Attributie). */
function groupBezoekenBySessions(bezoeken) {
  if (!Array.isArray(bezoeken) || bezoeken.length === 0) return [];
  const withDate = bezoeken.filter((b) => b && b.created_at != null);
  if (!withDate.length) return [];
  const sorted = [...withDate].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
  const SESSION_GAP_MS = 30 * 60 * 1000;
  const sessions = [];
  let current = [{ ...sorted[0], path: pathFromBezoek(sorted[0]) }];
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1].created_at).getTime();
    const curr = new Date(sorted[i].created_at).getTime();
    const path = pathFromBezoek(sorted[i]);
    if (Number.isNaN(prev) || Number.isNaN(curr) || curr - prev > SESSION_GAP_MS) {
      sessions.push(current);
      current = [{ ...sorted[i], path }];
    } else {
      current.push({ ...sorted[i], path });
    }
  }
  sessions.push(current);
  return sessions;
}

export default function Klanten360Profile({ profile, onUnlinkVisitor, unlinkBusyId }) {
  if (!profile) return null;

  // Nieuwste sessie bovenaan (groupBezoekenBySessions is chronologisch oud→nieuw)
  const visitSessions = groupBezoekenBySessions(profile.bezoeken || []).slice().reverse();
  const visitCount = profile.bezoeken?.length || 0;
  const visitorIds = Array.isArray(profile.visitor_ids) ? profile.visitor_ids.filter(Boolean) : [];
  const pendingFollowups = (profile.xendy?.browse_followups || []).filter((f) => f.status === 'pending');

  return (
    <div className="klanten360-profile">
      <header className="klanten360-header">
        <div>
          <span className={`klanten360-badge klanten360-badge-${profile.profile_type}`}>
            {profileLabel(profile.profile_type)}
          </span>
          <h2>{profile.klant_naam || profile.email}</h2>
          <p className="muted">{profile.email}</p>
        </div>
        <div className="klanten360-stats">
          <div>
            <span className="klanten360-stat-label">Orders</span>
            <strong>{profile.order_count}</strong>
          </div>
          <div>
            <span className="klanten360-stat-label">Omzet (regels)</span>
            <strong>€ {Number(profile.total_spent || 0).toFixed(2)}</strong>
          </div>
        </div>
      </header>

      {profile.popup ? (
        <section className="klanten360-block">
          <h3>Pop-up</h3>
          <p>{popupStatusLabel(profile.popup)}</p>
          {profile.popup.show_count > 0 ? (
            <p className="muted">{profile.popup.show_count}× getoond</p>
          ) : null}
          {profile.popup.last_shown_at ? (
            <p className="muted">Laatst getoond: {formatDateTimeNl(profile.popup.last_shown_at)}</p>
          ) : null}
          {profile.popup.last_dismissed_at ? (
            <p className="muted">Gesloten: {formatDateTimeNl(profile.popup.last_dismissed_at)}</p>
          ) : null}
        </section>
      ) : null}

      {profile.marketing && (
        <section className="klanten360-block">
          <h3>Marketing</h3>
          <p>
            Ingeschreven{' '}
            {formatDateTimeNl(profile.marketing.subscribed_at)}
            {' · '}
            via {signupSourceLabel(profile.marketing.signup_source)}
            {' · '}
            {profile.marketing.variant}
            {profile.marketing.subscriber_name ? ` · ${profile.marketing.subscriber_name}` : ''}
            {profile.marketing.coupon_code ? ` · code ${profile.marketing.coupon_code}` : ''}
            {profile.marketing.signup_page ? (
              <>
                {' '}
                · pagina {profile.marketing.signup_page}
              </>
            ) : null}
          </p>
        </section>
      )}

      {(profile.xendy?.mail_expectations?.length > 0 ||
        profile.xendy?.list_events?.length > 0 ||
        profile.xendy?.browse_followups?.length > 0) && (
        <section className="klanten360-block">
          <h3>Xendy &amp; mails</h3>
          {profile.xendy.mail_expectations?.length > 0 ? (
            <ul className="klanten360-mail-expect">
              {profile.xendy.mail_expectations.slice(0, 8).map((ex, i) => (
                <li key={`mx-${i}`}>
                  <strong>{ex.title}</strong>
                  {ex.at ? <span className="muted"> · {formatDateTimeNl(ex.at)}</span> : null}
                  {ex.detail ? <p className="muted">{ex.detail}</p> : null}
                  {ex.mail_hint ? <p className="klanten360-mail-hint">{ex.mail_hint}</p> : null}
                </li>
              ))}
            </ul>
          ) : null}
          {profile.xendy.list_events?.length > 0 ? (
            <>
              <h4 className="klanten360-subhead">Lijsten (Inkoop → Xendy)</h4>
              <ul className="klanten360-visits">
                {profile.xendy.list_events.slice(0, 12).map((ev) => (
                  <li key={ev.id}>
                    <time>{formatDateTimeNl(ev.created_at)}</time>
                    <span className="klanten360-url">
                      {ev.list_label}
                      <span className="muted"> · {ev.source_label}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {pendingFollowups.length > 0 ? (
            <div className="klanten360-unlink-block">
              <h4 className="klanten360-subhead">Openstaande follow-up</h4>
              <ul className="klanten360-unlink-list">
                {pendingFollowups.map((f) => (
                  <li key={`fu-${f.id}`}>
                    <span>
                      {formatDateTimeNl(f.scheduled_at)}
                      {f.visitor_id ? (
                        <span className="muted mpp-mono"> · {String(f.visitor_id).slice(0, 16)}…</span>
                      ) : null}
                    </span>
                    {onUnlinkVisitor && f.visitor_id ? (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={unlinkBusyId === f.visitor_id}
                        onClick={() => onUnlinkVisitor(f.visitor_id, profile.email)}
                      >
                        Ontkoppel browser
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      )}

      {visitorIds.length > 0 && onUnlinkVisitor ? (
        <section className="klanten360-block">
          <h3>Gekoppelde browsers</h3>
          <p className="muted">
            Verkeerd herkend (bijv. jouw PC na een klantorder)? Ontkoppel die browser hier. Orders blijven bestaan.
          </p>
          <ul className="klanten360-unlink-list">
            {visitorIds.map((vid) => (
              <li key={vid}>
                <code className="mpp-mono">{vid}</code>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={unlinkBusyId === vid}
                  onClick={() => onUnlinkVisitor(vid, profile.email)}
                >
                  Ontkoppel
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {profile.cart && (
        <section className="klanten360-block">
          <h3>Laatste winkelwagen</h3>
          <p className="muted klanten360-cart-time">
            Snapshot:{' '}
            {formatDateTimeNl(profile.cart.snapshot_at)}
          </p>
          {profile.cart.lines?.length ? (
            <ul>
              {profile.cart.lines.map((l, i) => (
                <li key={i}>
                  {l.quantity}× {l.name}
                  {l.line_total ? ` — € ${Number(l.line_total).toFixed(2)}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Geen items in laatste snapshot.</p>
          )}
        </section>
      )}

      {profile.profile_type === 'customer' && profile.orders?.length > 0 && (
        <section className="klanten360-block">
          <h3>Bestellingen &amp; gekocht</h3>
          <ul className="klanten360-orders">
            {profile.orders.map((o) => (
              <li key={o.woocommerce_order_id} className="klanten360-order">
                <div className="klanten360-order-head">
                  <strong>#{o.woocommerce_order_id}</strong>
                  <span>{o.verkoopdatum || '—'}</span>
                  <span>€ {Number(o.totaal_prijs || 0).toFixed(2)}</span>
                  {o.bron ? <span className="muted">{o.bron}</span> : null}
                </div>
                {o.lines?.length ? (
                  <ul className="klanten360-order-lines">
                    {o.lines.map((line, idx) => (
                      <li key={`${o.woocommerce_order_id}-${idx}`}>
                        {line.aantal}× {line.product_naam}
                        {line.product_sku ? <span className="muted"> ({line.product_sku})</span> : null}
                        {line.totaal_prijs != null ? (
                          <span className="klanten360-line-price">
                            {' '}
                            — € {Number(line.totaal_prijs).toFixed(2)}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted klanten360-order-empty">Geen regels in import.</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {profile.profile_type === 'customer' && !profile.orders?.length ? (
        <section className="klanten360-block">
          <h3>Bestellingen</h3>
          <p className="muted">Geen orders gevonden voor dit e-mailadres in verkoopdata.</p>
        </section>
      ) : null}

      {visitCount > 0 && (
        <section className="klanten360-block">
          <h3>
            Recente bezoeken ({visitCount}
            {profile.visitor_ids?.length > 1
              ? ` · ${profile.visitor_ids.length} browsers`
              : ''}
            )
          </h3>
          <p className="muted">
            Alleen pagina&apos;s van browsers gekoppeld aan dit e-mailadres (inschrijving of order).
          </p>
          {visitSessions.map((session, idx) => (
            <div key={`sess-${idx}`} className="klanten360-session">
              {visitSessions.length > 1 ? (
                <p className="klanten360-session-head muted">
                  Sessie {idx + 1} – {session.length} pagina{session.length !== 1 ? "'s" : ''} –{' '}
                  {formatDateTimeNl(session[session.length - 1]?.created_at || session[0]?.created_at)}
                </p>
              ) : null}
              <ul className="klanten360-visits">
                {[...session].reverse().slice(0, 40).map((b) => (
                  <li key={b.id}>
                    <time>{formatDateTimeNl(b.created_at)}</time>
                    <span className="klanten360-url">{b.path}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
